import React, { useEffect, useState } from "react";
import { UserPlus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ROLE_COLORS = { admin: "bg-red-100 text-red-700", editor: "bg-blue-100 text-blue-700", journalist: "bg-orange-100 text-orange-700", contributor: "bg-gray-100 text-gray-600" };
const emptyUser = { name: "", email: "", password: "", role: "journalist", active: true };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyUser);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/admin/users"), api.get("/admin/roles")]);
      setUsers(u.data.items); setRoles(r.data.roles);
    } catch (e) { toast.error("No se pudieron cargar los usuarios"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(emptyUser); setOpen(true); };
  const openEdit = (u) => { setEditId(u.id); setForm({ name: u.name, email: u.email, password: "", role: u.role, active: u.active }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      if (editId) { await api.put(`/admin/users/${editId}`, form); toast.success("Usuario actualizado"); }
      else { await api.post("/admin/users", form); toast.success("Usuario creado"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <AdminLayout title="Gestión de redacción">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">Administrá los usuarios de la redacción y sus roles.</p>
        <button onClick={openNew} data-testid="new-user-btn" className="inline-flex items-center gap-2 nx-gradient-bg text-white font-bold px-4 py-2 rounded-md text-xs uppercase tracking-wider"><UserPlus size={15} /> Nuevo usuario</button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="px-4 py-3">Nombre</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Rol</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3 text-right">Acciones</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Cargando…</td></tr>
              : users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-nx-dark">{u.name}</td>
                  <td className="px-4 py-3 text-gray-500">{u.email}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] font-bold uppercase px-2 py-1 rounded ${ROLE_COLORS[u.role]}`}>{u.role_label}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs ${u.active ? "text-green-600" : "text-gray-400"}`}>{u.active ? "Activo" : "Inactivo"}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="p-2 rounded hover:bg-gray-100 text-gray-500"><Pencil size={16} /></button>
                      {u.id !== me?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><button className="p-2 rounded hover:bg-red-50 text-red-500" data-testid={`delete-user-${u.id}`}><Trash2 size={16} /></button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle><AlertDialogDescription>Se eliminará a {u.name} ({u.email}).</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(u.id)} data-testid={`confirm-delete-user-${u.id}`} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Roles reference */}
      <h3 className="font-serif font-black text-lg text-nx-dark mb-3 flex items-center gap-2"><ShieldCheck size={18} /> Roles y permisos</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {roles.map((r) => (
          <div key={r.key} className="bg-white border border-gray-200 rounded-lg p-4">
            <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded ${ROLE_COLORS[r.key]}`}>{r.label}</span>
            <div className="mt-3 flex flex-wrap gap-1">
              {r.permissions.map((p) => <span key={p} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{p}</span>)}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editId ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>Definí nombre, email, contraseña y rol del integrante de la redacción.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name" className="mt-1.5" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email" className="mt-1.5" disabled={!!editId} /></div>
            <div><Label>Contraseña {editId && <span className="text-xs text-gray-400">(dejar vacío para no cambiar)</span>}</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password" className="mt-1.5" /></div>
            <div><Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2"><Checkbox id="active" checked={form.active} onCheckedChange={(v) => set("active", !!v)} data-testid="user-active" /><Label htmlFor="active" className="font-normal">Usuario activo</Label></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="border border-gray-300 rounded-md px-4 py-2 text-sm font-bold">Cancelar</button>
            <button onClick={save} data-testid="save-user" className="nx-gradient-bg text-white rounded-md px-4 py-2 text-sm font-bold">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
